import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { describeApiError } from "../../api/errors";
import { keys, useHmrcVerification, useJob, useMigration, useRerunHmrc } from "../../api/hooks";
import { Badge, Banner, Button, ConfirmDialog } from "../../components";
import { t } from "../../i18n/t";

const TERMINAL_JOB_STATUSES: ReadonlySet<string> = new Set(["succeeded", "failed", "cancelled", "aborted"]);

/** Critique addendum C3: the bounded replay-window refetch schedule -- three ticks, then stop. */
const REPLAY_REFETCH_DELAYS_MS: readonly number[] = [2000, 5000, 10000];

/**
 * hmrc-rerun-action design §3.6/§5.4, critique addendum C2/C3/C9/C10/C12/C13. This panel owns every query
 * and every mutation (the same discipline `FailurePanel`/`AttachmentsPanel` already follow): mounted on the
 * Review screen (C2, `awaiting_mapping_review`'s only reachable placement), the Dry-run screen and the
 * Status screen (status-only there -- the button never renders once the migration has moved past
 * `awaiting_conversion_signoff`, because `view.rerun.allowed` is then false by construction, C9).
 *
 * Renders nothing when HMRC is not attached (V1) -- the ONE case this component itself decides without
 * reading `rerun.reason` at all, per C9.
 */
export function HmrcVerificationPanel({ migrationId }: { migrationId: string }): JSX.Element | null {
  const qc = useQueryClient();
  const migration = useMigration(migrationId);
  const verification = useHmrcVerification(migrationId);
  const rerun = useRerunHmrc(migrationId);
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [jobId, setJobId] = useState<number | null>(null);
  const [jobFailed, setJobFailed] = useState(false);
  const job = useJob(jobId);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  function invalidateAfterRerun(): void {
    void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "hmrc-verification") });
    void qc.invalidateQueries({ queryKey: keys.migration(migrationId) });
    void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "events") });
  }

  // Critique addendum C3: on a terminal job status, invalidate and stop polling this job.
  useEffect(() => {
    const status = job.data?.status;
    if (jobId === null || status === undefined || !TERMINAL_JOB_STATUSES.has(status)) return;
    invalidateAfterRerun();
    if (status === "failed") setJobFailed(true);
    setJobId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.data?.status, jobId]);

  // MEDIUM finding fix (C3 replay window): a `job_id: null` 202 (an `AlreadyEnqueued` collision after
  // commit, or a replay between commit and the job_id update) has no job to poll, so refetch the view at
  // 2s/5s/10s instead, then stop. Started directly from `onConfirm` (never from an effect keyed on a
  // state flag this same call flips straight back to false): that pattern made the effect's OWN cleanup
  // clear the timers it had just set, on the very next render, before any of them fired. Timers live in
  // the ref and are cleared only on unmount or the next trigger.
  function startReplayRefetch(): void {
    timers.current.forEach(clearTimeout);
    timers.current = REPLAY_REFETCH_DELAYS_MS.map((delay) =>
      setTimeout(() => {
        void verification.refetch();
        void qc.invalidateQueries({ queryKey: keys.migration(migrationId) });
        void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "events") });
      }, delay),
    );
  }

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const view = verification.data;
  if (view === undefined || !view.attached) {
    return null;
  }

  const state = migration.data?.state;
  const atConversionSignoff = state === "awaiting_conversion_signoff";

  async function onConfirm(): Promise<void> {
    setFailure(null);
    try {
      const accepted = await rerun.mutateAsync({ expected_run_id: view?.run_id ?? null });
      setConfirming(false);
      setJobFailed(false);
      const jobIdOrNull = accepted.job_id ?? null;
      if (jobIdOrNull === null) {
        startReplayRefetch();
      } else {
        setJobId(jobIdOrNull);
      }
    } catch (caught) {
      setConfirming(false);
      setFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  const headline =
    view.status === "not_run"
      ? t("hmrc_verification.not_run")
      : view.status === "completed" && !view.gap
        ? view.trigger === "operator_rerun"
          ? t("hmrc_verification.rerun_at", { at: view.at ?? "" })
          : t("hmrc_verification.verified_at", { at: view.at ?? "" })
        : view.status === "completed"
          ? t("hmrc_verification.incomplete")
          : t("hmrc_verification.failed");

  const jobActive = jobId !== null && job.data !== undefined && !TERMINAL_JOB_STATUSES.has(job.data.status);
  const reconnectReasons: ReadonlySet<string> = new Set([
    "hmrc_attestation_unusable",
    "hmrc_connection_revoked",
  ]);
  // LOW finding fix: `hmrc_attestation_unusable` also covers E14 (`LUCIMIG_TOKEN_KEY` unusable, a server
  // configuration problem whose message says "contact support") -- `missing` is the only way to tell that
  // case apart from a genuinely stale/unusable attestation (E12/E13), which "Reconnect HMRC" cannot fix.
  const showReconnect =
    !view.rerun.allowed &&
    typeof view.rerun.reason === "string" &&
    reconnectReasons.has(view.rerun.reason) &&
    !(view.rerun.missing ?? []).includes("server_configuration");

  return (
    <section data-testid="hmrc-verification-panel">
      <p>
        {t("hmrc_verification.status_label")} <Badge tone={view.gap ? "warn" : "pass"}>{headline}</Badge>
      </p>

      {failure === null ? null : <Banner tone="block">{failure}</Banner>}
      {jobFailed ? <Banner tone="warn">{t("hmrc_verification.job_failed")}</Banner> : null}
      {!view.rerun.allowed && view.rerun.message !== null && !showReconnect ? (
        <p>{view.rerun.message}</p>
      ) : null}
      {showReconnect ? (
        <p>
          {view.rerun.message}{" "}
          <a href={`/migrations/${migrationId}/connect/hmrc`}>{t("hmrc_verification.reconnect")}</a>
        </p>
      ) : null}

      {view.rerun.allowed ? (
        <>
          <Button
            variant="primary"
            disabled={jobActive}
            onClick={() => {
              setFailure(null);
              setConfirming(true);
            }}
          >
            {t("hmrc_verification.rerun_button")}
          </Button>
          <ConfirmDialog
            open={confirming}
            title={t("hmrc_verification.confirm_title")}
            onCancel={() => setConfirming(false)}
            onConfirm={() => void onConfirm()}
          >
            {atConversionSignoff ? (
              <>
                <p>{t("hmrc_verification.confirm_body_conversion_1")}</p>
                <p>{t("hmrc_verification.confirm_body_conversion_2")}</p>
                <p>{t("hmrc_verification.confirm_body_conversion_3")}</p>
              </>
            ) : (
              <p>{t("hmrc_verification.confirm_body_mapping")}</p>
            )}
          </ConfirmDialog>
        </>
      ) : null}
    </section>
  );
}
